import type { MarketResolved, MarketTrade } from '@morfi/proto';
import type { ScopedSnapshotSequence } from '../nats/scoped-snapshot-sequence.js';
import { base64ToCursor, compareCursor, cursorToBase64 } from '../nats/sequence-id.js';
import {
  CONVICTION_CHART_LIVE_DETAIL_WINDOW_MS,
  type ConvictionChartEventInput,
  type ConvictionChartSnapshotInput,
  type ConvictionChartStateInput,
} from './conviction-chart.js';
import {
  type ConvictionChartInterval,
  convictionChartIntervalSeconds,
} from './conviction-chart-request.js';

type ConvictionChartTradeEvent = Pick<
  MarketTrade,
  'sequenceId' | 'ts' | 'postOutcomeConvictionPpb' | 'postOutcomeConvictionTargetPpb'
>;

type ConvictionChartResolutionEvent = Pick<MarketResolved, 'ts'>;

interface ConvictionChartRealtimeArgs {
  readonly interval: ConvictionChartInterval;
  readonly from: string;
  readonly to?: string;
}

/**
 * HARD RULE: HTTP and NATS overlap and NATS can be duplicate or out of order.
 * NATS `sequenceId` orders live states; REST buckets have no event cursor.
 * `ts` is the contract's whole-second effective time, never receipt time.
 * Keep one complete state per bucket; never merge outcome vectors.
 */
export function applyConvictionChartTrade<T extends ConvictionChartSnapshotInput>(
  previous: T,
  event: ConvictionChartTradeEvent,
  args: ConvictionChartRealtimeArgs,
): T {
  if (args.interval !== previous.interval) return previous;
  const state = readTradeState(event);
  if (state === null) return previous;
  const sourceEventMs = Date.parse(state.sourceEventTs);
  const fromMs = Date.parse(previous.from);
  const previousEffectiveToMs = Date.parse(previous.effectiveTo);
  const explicitToMs = args.to === undefined ? null : Date.parse(args.to);
  if (
    !Number.isFinite(sourceEventMs) ||
    !Number.isFinite(fromMs) ||
    !Number.isFinite(previousEffectiveToMs)
  ) {
    return previous;
  }
  if (sourceEventMs < previousEffectiveToMs - CONVICTION_CHART_LIVE_DETAIL_WINDOW_MS) {
    return previous;
  }
  if (explicitToMs !== null && (!Number.isFinite(explicitToMs) || sourceEventMs >= explicitToMs)) {
    return previous;
  }

  if (sourceEventMs < fromMs) {
    const seed = laterState(previous.seed, state);
    return seed === previous.seed ? previous : ({ ...previous, seed } as T);
  }

  const bucketSeconds = convictionChartIntervalSeconds(previous.interval);
  const bucketStartMs = Math.floor(sourceEventMs / (bucketSeconds * 1_000)) * bucketSeconds * 1_000;
  const candidate: ConvictionChartEventInput = {
    ...state,
    bucketStart: new Date(bucketStartMs).toISOString(),
  };
  const events = replaceBucketEvent(previous.events, candidate);
  if (events === previous.events) return previous;
  const effectiveToMs =
    explicitToMs === null
      ? Math.max(previousEffectiveToMs, sourceEventMs + 1_000)
      : previousEffectiveToMs;
  return {
    ...previous,
    effectiveTo: new Date(effectiveToMs).toISOString(),
    events,
  } as T;
}

export function applyConvictionChartResolution<T extends ConvictionChartSnapshotInput>(
  previous: T,
  event: ConvictionChartResolutionEvent,
  args: ConvictionChartRealtimeArgs,
): T {
  const resolvedAtSecs = safeUnixSeconds(event.ts);
  if (resolvedAtSecs === null) return previous;
  const resolvedAtMs = resolvedAtSecs * 1_000;
  const terminalToMs = resolvedAtMs + 1_000;
  const previousToMs = Date.parse(previous.effectiveTo);
  const explicitToMs = args.to === undefined ? null : Date.parse(args.to);
  if (
    !Number.isFinite(previousToMs) ||
    (explicitToMs !== null && (!Number.isFinite(explicitToMs) || resolvedAtMs >= explicitToMs))
  ) {
    return previous;
  }
  const events = previous.events.filter((event) => Date.parse(event.sourceEventTs) < terminalToMs);
  const effectiveToMs = explicitToMs === null ? terminalToMs : Math.min(terminalToMs, explicitToMs);
  if (effectiveToMs === previousToMs && events.length === previous.events.length) return previous;
  return {
    ...previous,
    effectiveTo: new Date(effectiveToMs).toISOString(),
    events,
  } as T;
}

export function convictionChartSnapshotSequence(
  snapshot: ConvictionChartSnapshotInput | undefined,
  subject: string,
): ScopedSnapshotSequence {
  if (snapshot === undefined) return { kind: 'unhandled' };
  if (!subject.includes('.trade.v0') && !subject.endsWith('.resolution.v0')) {
    return { kind: 'unhandled' };
  }
  const cursor = base64ToCursor(snapshot.lastSeq.sequenceId);
  if (snapshot.lastSeq.sequenceId === null) return { kind: 'empty' };
  return cursor === null ? { kind: 'invalid' } : { kind: 'cursor', cursor };
}

function readTradeState(event: ConvictionChartTradeEvent): ConvictionChartStateInput | null {
  const sequenceId = cursorToBase64(event.sequenceId);
  const sourceEventSecs = safeUnixSeconds(event.ts);
  const anchors = readPpbVector(event.postOutcomeConvictionPpb);
  const targets = readPpbVector(event.postOutcomeConvictionTargetPpb);
  if (
    sequenceId === null ||
    sourceEventSecs === null ||
    anchors === null ||
    targets === null ||
    anchors.length === 0 ||
    anchors.length !== targets.length
  ) {
    return null;
  }
  return {
    sourceEventTs: new Date(sourceEventSecs * 1_000).toISOString(),
    sequenceId,
    anchorsPpb: anchors,
    targetsPpb: targets,
  };
}

function readPpbVector(values: ReadonlyArray<number>): ReadonlyArray<string> | null {
  if (
    !values.every((value) => Number.isSafeInteger(value) && value >= 0 && value <= 1_000_000_000)
  ) {
    return null;
  }
  return values.map(String);
}

function safeUnixSeconds(value: bigint): number | null {
  return value >= 0n && value <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(value) : null;
}

function laterState(
  existing: ConvictionChartStateInput | null,
  candidate: ConvictionChartStateInput,
): ConvictionChartStateInput {
  if (existing === null) return candidate;
  return isNewerState(candidate, existing) ? candidate : existing;
}

function replaceBucketEvent(
  events: ReadonlyArray<ConvictionChartEventInput>,
  candidate: ConvictionChartEventInput,
): ReadonlyArray<ConvictionChartEventInput> {
  const duplicate = events.find(
    (event) => event.sequenceId !== undefined && event.sequenceId === candidate.sequenceId,
  );
  if (duplicate !== undefined) return events;
  const index = events.findIndex((event) => event.bucketStart === candidate.bucketStart);
  if (index >= 0) {
    const existing = events[index] as ConvictionChartEventInput;
    if (!isNewerState(candidate, existing)) return events;
    return events.map((event, eventIndex) => (eventIndex === index ? candidate : event));
  }
  return [...events, candidate].sort(
    (left, right) => Date.parse(left.bucketStart) - Date.parse(right.bucketStart),
  );
}

function isNewerState(
  candidate: ConvictionChartStateInput,
  existing: ConvictionChartStateInput,
): boolean {
  if (candidate.sequenceId !== undefined && existing.sequenceId !== undefined) {
    return compareSequenceId(candidate.sequenceId, existing.sequenceId) > 0;
  }
  const candidateMs = Date.parse(candidate.sourceEventTs);
  const existingMs = Date.parse(existing.sourceEventTs);
  if (candidateMs !== existingMs) return candidateMs > existingMs;
  return candidate.sequenceId !== undefined && existing.sequenceId === undefined;
}

function compareSequenceId(left: string, right: string): number {
  const leftCursor = base64ToCursor(left);
  const rightCursor = base64ToCursor(right);
  if (leftCursor === null || rightCursor === null) return 0;
  return compareCursor(leftCursor, rightCursor);
}
